import { useCallback, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Device location helper. `capture()` asks the browser for GPS coordinates,
 * saves them to the user's profile, and returns {lat, lng}. Falls back to the
 * profile's saved location when GPS is denied/unavailable.
 */
const useLocation = () => {
  const { user, updateUser } = useAuth();
  const [busy, setBusy] = useState(false);

  const capture = useCallback(() => {
    setBusy(true);
    return new Promise((resolve) => {
      const fallback = () => {
        setBusy(false);
        resolve(user?.lat && user?.lng ? { lat: Number(user.lat), lng: Number(user.lng) } : null);
      };

      if (!navigator.geolocation) return fallback();

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          try {
            await api.patch('/api/users/me', { lat, lng });
            updateUser({ ...user, lat, lng });
          } catch {
            /* saving is best-effort — the coordinates are still usable */
          }
          setBusy(false);
          resolve({ lat, lng });
        },
        fallback,
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    });
  }, [user, updateUser]);

  const saved = user?.lat && user?.lng ? { lat: Number(user.lat), lng: Number(user.lng) } : null;

  return { capture, saved, busy };
};

export default useLocation;
